import React, { useState, useEffect, useRef } from 'react';
import { analytics } from '../utils/analytics';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Link,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Alert,
  AlertTitle,
  LinearProgress,
  Chip,
  CircularProgress,
  Card,
  CardMedia,
  Stack,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GavelIcon from '@mui/icons-material/Gavel';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RealEstateAgentIcon from '@mui/icons-material/RealEstateAgent';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import BathtubOutlinedIcon from '@mui/icons-material/BathtubOutlined';
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { OAuthButtonGroup } from '../components/OAuthButton';
import useOAuth from '../hooks/useOAuth';
import api from '../services/api'; // Use centralized API service
import { useNotification } from '../contexts/NotificationContext';
import DotGridBackground from '../components/DotGridBackground';
import PropertyCreationProgressModal from '../components/PropertyCreationProgressModal';
import usePropertyCreationProgress from '../hooks/usePropertyCreationProgress';
import { readStoredOutreachRef, storeOutreachRef } from '../utils/outreachRef';

// Account types offered at signup. Agent and Lawyer only *request* the role;
// KeyTa grants it after reviewing the licence, so the copy says so up front.
// Each type carries its own accent from the brand gradient (teal / green / bronze)
// so the selected tile reads as "that kind of account", not just "selected".
const SIGNUP_ROLES = [
  {
    value: 'Buyer/Seller',
    label: 'Buyer / Seller',
    tagline: 'Buy or sell a home',
    icon: <HomeIcon />,
    accent: '#1f6f8b',
    description: 'Search listings, get AI guidance and list your own home — no licence needed.',
  },
  {
    value: 'Agent',
    label: 'Agent',
    tagline: 'Licensed REALTOR®',
    icon: <RealEstateAgentIcon />,
    accent: '#2ca58d',
    description: 'CRM, MLS-ready listings and marketing tools. KeyTa reviews your licence after signup.',
  },
  {
    value: 'Lawyer',
    label: 'Lawyer',
    tagline: 'Real estate law',
    icon: <GavelIcon />,
    accent: '#bb8b57',
    description: 'Transaction files, documents and closings. KeyTa reviews your licence after signup.',
  },
];

const Signup = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    primary_role: 'Buyer/Seller',
    license_number: '',
    brokerage: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  // 'agent' | 'lawyer' when the signup asked for a professional role that is now awaiting review
  const [requestedRole, setRequestedRole] = useState(null);
  const [verificationCodeDigits, setVerificationCodeDigits] = useState(['', '', '', '', '', '']);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const verificationInputsRef = useRef([]);
  const [outreachRefCode, setOutreachRefCode] = useState(null);
  const [outreachData, setOutreachData] = useState(null);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const propertyCreationProgress = usePropertyCreationProgress();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { availableProviders, initiateOAuth, loading: oauthLoading, error: oauthError } = useOAuth();

  // Detect outreach attribution (?outreach_ref= or sessionStorage) and fetch
  // a lightweight property snapshot so we can render a personalized teaser:
  // "you're one step away from relisting <address>".
  useEffect(() => {
    let code = null;
    try {
      const urlRef = new URLSearchParams(window.location.search).get('outreach_ref');
      if (urlRef && urlRef.trim()) {
        // Promote the URL param to sessionStorage so it survives the Google
        // OAuth round-trip and the email verification step.
        code = storeOutreachRef(urlRef) || urlRef.trim().toUpperCase();
      } else {
        code = readStoredOutreachRef();
      }
    } catch (_) { /* sessionStorage unavailable */ }

    if (!code) return;
    setOutreachRefCode(code);
    // A mailer/QR recipient is the homeowner of the targeted property: they are
    // signing up to sell, so the account type is decided for them.
    setFormData((prev) => ({ ...prev, primary_role: 'Buyer/Seller', client_type: 'Seller', license_number: '', brokerage: '' }));

    let cancelled = false;
    setOutreachLoading(true);
    (async () => {
      try {
        const response = await api.get(`/outreach/landing/${code}`, { skipAuthHandling: true });
        if (!cancelled) setOutreachData(response);
      } catch (_) {
        // Silent fail — teaser is a nice-to-have, signup must still work.
        if (!cancelled) setOutreachData(null);
      } finally {
        if (!cancelled) setOutreachLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Check password strength in real-time
  useEffect(() => {
    if (formData.password) {
      checkPasswordStrength(formData.password);
    } else {
      setPasswordStrength(null);
    }
  }, [formData.password]);

  const checkPasswordStrength = async (pwd) => {
    try {
      const response = await api.post('/password/check-strength', { password: pwd });
      const data = response.data || response; // Handle both new and legacy formats
      setPasswordStrength(data);
    } catch (error) {
      console.error('Password strength check failed:', error);
    }
  };

  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 'Strong': return 'success';
      case 'Medium': return 'warning';
      case 'Weak': return 'error';
      default: return 'default';
    }
  };

  const getPasswordStrengthProgress = (score, maxScore) => {
    return (score / maxScore) * 100;
  };

  const handleOAuthSignup = (provider) => {
    if (oauthLoading) return;
    analytics.oauthSignUpClick(provider);
    try {
      // The "I am a" choice is made before the Google button, so it travels with
      // the redirect. Outreach recipients are always sellers.
      const role = !outreachRefCode && (formData.primary_role === 'Agent' || formData.primary_role === 'Lawyer')
        ? formData.primary_role.toLowerCase()
        : undefined;
      initiateOAuth(provider, { role });
    } catch (error) {
      showNotification(`Failed to initiate ${provider} signup`, 'error');
    }
  };

  // Show OAuth error if any
  useEffect(() => {
    if (oauthError) {
      showNotification(oauthError, 'error');
    }
  }, [oauthError, showNotification]);

  const validateForm = () => {
    const newErrors = {};
    
    // Existing validations
    if (!formData.first_name?.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    // Professional accounts are reviewed by an admin, who needs the licence to check.
    if ((formData.primary_role === 'Agent' || formData.primary_role === 'Lawyer') && !formData.license_number?.trim()) {
      newErrors.license_number = 'License number is required so we can verify your account';
    }

    // Add terms validation
    if (!acceptTerms) {
      newErrors.terms = 'You must accept the Terms of Service and Privacy Policy';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
  
    try {
      // Attach outreach attribution code if user landed here via a QR / mailer.
      // Captured on mount via useEffect (URL param promoted to sessionStorage).
      const outreachRef = outreachRefCode || readStoredOutreachRef();

      const payload = outreachRef
        ? { ...formData, outreach_ref: outreachRef }
        : formData;

      const response = await api.postRaw('/auth/signup', payload);
      console.log('Signup response:', response);
      const envelope = response?.data || response || {};
      const signupData = envelope?.data && typeof envelope.data === 'object'
        ? envelope.data
        : envelope;
      const signupMessage = envelope?.message || signupData?.message;

      if (signupData.token) {
        // Add permissions to user object before passing to login
        const userWithPermissions = {
          ...signupData.user,
          permissions: signupData.permissions || []
        };
        
        // Use secure login method from AuthContext
        await login(userWithPermissions, signupData.token);
        analytics.signUp('email');
        
        // Show success message
        showNotification('Registration successful! Redirecting...', 'success');
        
        // Redirect after a short delay
        setTimeout(() => navigate('/dashboard'), 1500);
      } else if (signupData.verification_token) {
        setVerificationToken(signupData.verification_token);
        setRequestedRole(signupData.requested_role || null);
        setVerificationEmail(signupData.email || formData.email);
        setVerificationCodeDigits(['', '', '', '', '', '']);
        showNotification(
          signupMessage || 'Registration successful. Enter the 6-digit code sent to your email.',
          'success'
        );
      }
      if (!signupData.verification_token && signupMessage) {
        showNotification(signupMessage, 'success');
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      
      // Show error in snackbar
      showNotification(errorMessage, 'error');

      // If the error is email-specific, also show it in the form
      if (errorMessage.toLowerCase().includes('email')) {
        setErrors(prev => ({
          ...prev,
          email: errorMessage
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationDigitChange = (index, rawValue) => {
    const digit = (rawValue || '').replace(/\D/g, '').slice(-1);

    if (verificationError) {
      setVerificationError('');
    }

    setVerificationCodeDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < 5) {
      verificationInputsRef.current[index + 1]?.focus();
    }
  };

  const handleVerificationDigitKeyDown = (index, event) => {
    if (verificationError) {
      setVerificationError('');
    }
    if (event.key === 'Backspace') {
      if (verificationCodeDigits[index]) {
        setVerificationCodeDigits((prev) => {
          const next = [...prev];
          next[index] = '';
          return next;
        });
      } else if (index > 0) {
        verificationInputsRef.current[index - 1]?.focus();
        setVerificationCodeDigits((prev) => {
          const next = [...prev];
          next[index - 1] = '';
          return next;
        });
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      verificationInputsRef.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < 5) {
      verificationInputsRef.current[index + 1]?.focus();
    }
  };

  const handleVerificationCodePaste = (event) => {
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) {
      return;
    }

    event.preventDefault();
    if (verificationError) {
      setVerificationError('');
    }
    const nextDigits = ['', '', '', '', '', ''];
    pasted.split('').forEach((digit, idx) => {
      nextDigits[idx] = digit;
    });
    setVerificationCodeDigits(nextDigits);

    const focusIndex = Math.min(pasted.length, 5);
    verificationInputsRef.current[focusIndex]?.focus();
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    const normalizedCode = verificationCodeDigits.join('').replace(/\D/g, '');
    if (normalizedCode.length !== 6) {
      showNotification('Please enter a valid 6-digit verification code.', 'error');
      return;
    }

    if (!verificationToken) {
      showNotification('Verification session expired. Please sign up again.', 'error');
      return;
    }

    setIsVerifyingCode(true);
    setVerificationError('');
    try {
      // Attach outreach attribution so the property is seeded now that the
      // account is verified (moved out of signup to avoid orphan properties).
      const outreachRef = outreachRefCode || readStoredOutreachRef();

      const response = await api.post('/auth/verify-email-code', {
        token: verificationToken,
        code: normalizedCode,
      });
      const data = response?.data || response;

      // Log the user in first so the follow-up seed call is authenticated.
      if (data?.token) {
        const userWithPermissions = {
          ...data.user,
          permissions: data.permissions || [],
        };

        await login(userWithPermissions, data.token);
        analytics.signUp('email');
      }

      // Seed the outreach property as a separate authenticated step so the
      // progress modal animates during the real work. This runs only after the
      // code is confirmed valid, so an invalid code never shows this modal.
      if (outreachRef) {
        try {
          // Shared with the Google OAuth callback so both signup paths seed
          // the property the same way (see usePropertyCreationProgress).
          const { propertyCreated } = await propertyCreationProgress.seedFromOutreach(outreachRef, {
            title: 'Creating your property from MLS history',
            subtitle: 'We are creating your listing and preparing the core materials.',
          });

          if (propertyCreated) {
            // Results phase; user confirms to open the new property dashboard.
            showNotification('Email verified. Your property is ready!', 'success');
            return;
          }

          // Nothing to seed — continue to the dashboard.
          showNotification('Email verified. Redirecting to dashboard...', 'success');
          navigate('/dashboard');
          return;
        } catch (seedErr) {
          console.error('Outreach property seeding failed:', seedErr);
          showNotification('Email verified, but we could not prepare your property automatically.', 'warning');
          navigate('/dashboard');
          return;
        }
      }

      if (data?.token) {
        if (requestedRole) {
          showNotification(
            `Email verified. Your ${requestedRole} account is under review — you can use KeyTa as a client until it is approved.`,
            'info'
          );
        } else {
          showNotification('Email verified. Redirecting to dashboard...', 'success');
        }
        navigate('/dashboard');
        return;
      }

      showNotification('Verification completed, but login data was missing. Please log in manually.', 'warning');
      navigate('/login');
    } catch (err) {
      // Keep the user on the code step: mark the boxes red and show why.
      const errorMessage = err.message || 'Invalid or expired verification code. Please try again.';
      setVerificationError(errorMessage);
      verificationInputsRef.current[0]?.focus();
      showNotification(errorMessage, 'error');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    const email = verificationEmail || formData.email;
    if (!email) {
      showNotification('Unable to resend code. Please start signup again.', 'error');
      return;
    }

    setIsVerifyingCode(true);
    try {
      const response = await api.postRaw('/auth/resend-verification-email', { email });
      const envelope = response?.data || response || {};
      const responseData = envelope?.data && typeof envelope.data === 'object'
        ? envelope.data
        : envelope;

      if (responseData?.verification_token) {
        setVerificationToken(responseData.verification_token);
        setVerificationCodeDigits(['', '', '', '', '', '']);
        setVerificationEmail(responseData.email || email);
        verificationInputsRef.current[0]?.focus();
      }

      showNotification(
        envelope?.message || responseData?.message || 'A new verification code has been sent.',
        'success'
      );
    } catch (err) {
      const errorMessage = err.message || 'Failed to resend verification code. Please try again.';
      showNotification(errorMessage, 'error');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100dvh',
        background: theme.modern.backgrounds.gradient,
        position: 'relative',
        overflowX: 'hidden',
        width: '100%',
      }}
    >
      <DotGridBackground />
      <Header />
      <Box sx={{ pt: { xs: 8, sm: 9 }, px: { xs: 0, sm: 2 }, position: 'relative', zIndex: 2 }}>
        <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 8 }, px: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: 'center' }}>
            {/* <RouterLink to="/" style={{ textDecoration: 'none' }}> */}
              <Typography
                variant="h3"
                component="div"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  ...theme.modern.utils.gradientText('primary'),
                  // fontWeight: 500,
                  mb: 1,
                  fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                {/* <HomeIcon sx={{ color: theme.modern.colors.modern.mainColor[500] }} /> */}
                Create Your Account
              </Typography>
            {/* </RouterLink> */}
            
            <Typography 
              variant="body1" 
              sx={{ 
                ...theme.modern.typography.modernSubtext 
              }}
            >
              {outreachRefCode
                ? "Create your profile to instantly take ownership and relist — easy, fast, on your terms"
                : 'Join to start your real estate journey'}
            </Typography>
          </Box>

          {outreachRefCode && outreachData && (
            (() => {
              const target = outreachData?.target || {};
              const listing = outreachData?.listing || null;
              const photos = Array.isArray(outreachData?.photos) ? outreachData.photos : [];
              const heroImage = photos[0];
              const address = target.address || null;
              const subAddress = [target.neighborhood, target.city, target.postal_code]
                .filter(Boolean)
                .join(' · ');
              const price = listing?.list_price ?? target.latest_list_price ?? null;
              const formatPrice = (v) => {
                if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
                try {
                  return new Intl.NumberFormat('en-CA', {
                    style: 'currency',
                    currency: 'CAD',
                    maximumFractionDigits: 0,
                  }).format(Number(v));
                } catch (_) {
                  return `$${Number(v).toLocaleString()}`;
                }
              };
              const beds = listing?.bedrooms_total ?? null;
              const baths = listing?.bathrooms_total ?? null;
              const area = listing?.living_area_range || null;
              const accent = theme?.modern?.colors?.modern?.mainColor?.[500] || theme.palette.primary.main;

              return (
                <Card
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    border: `1px solid ${alpha(accent, 0.25)}`,
                    // background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`,
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="stretch">
                    {heroImage ? (
                      <CardMedia
                        component="img"
                        image={heroImage}
                        alt={address || 'Your property'}
                        sx={{
                          width: { xs: '100%', sm: 180 },
                          height: { xs: 180, sm: 'auto' },
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: { xs: '100%', sm: 180 },
                          minHeight: { xs: 140, sm: 'auto' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(accent, 0.08),
                          color: accent,
                        }}
                      >
                        <HomeIcon sx={{ fontSize: 48 }} />
                      </Box>
                    )}

                    <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ mb: 1 }}
                      >
                        <Chip
                          icon={<BoltIcon sx={{ fontSize: 16 }} />}
                          label="1 step away"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            bgcolor: accent,
                            color: '#fff',
                            '& .MuiChip-icon': { color: '#fff' },
                          }}
                        />
                      </Stack>

                      <>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              lineHeight: 1.25,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {address || 'Your property is ready to relaunch'}
                          </Typography>

                          {subAddress && (
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {subAddress}
                              </Typography>
                            </Stack>
                          )}

                          {/* <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}
                          >
                            {price && (
                              <Chip
                                size="small"
                                label={formatPrice(price)}
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: alpha(accent, 0.12),
                                  color: accent,
                                }}
                              />
                            )}
                            {beds ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                icon={<BedOutlinedIcon sx={{ fontSize: 16 }} />}
                                label={`${beds} bed`}
                              />
                            ) : null}
                            {baths ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                icon={<BathtubOutlinedIcon sx={{ fontSize: 16 }} />}
                                label={`${baths} bath`}
                              />
                            ) : null}
                            {area && (
                              <Chip
                                size="small"
                                variant="outlined"
                                icon={<SquareFootOutlinedIcon sx={{ fontSize: 16 }} />}
                                label={area}
                              />
                            )}
                          </Stack> */}

                          {/* <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            sx={{ mt: 1.25 }}
                          >
                            <AutoAwesomeIcon sx={{ fontSize: 16, color: accent }} />
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', lineHeight: 1.35 }}
                            >
                              Create your profile to instantly take ownership and relist —
                              easy, fast, on your terms.
                            </Typography>
                          </Stack> */}
                        </>
                    </Box>
                  </Stack>
                </Card>
              );
            })()
          )}

          {apiError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: '0.5rem',
                '& .MuiAlert-message': { width: '100%' }
              }}
              onClose={() => setApiError(null)}
            >
              <AlertTitle>Registration Failed</AlertTitle>
              {apiError}
            </Alert>
          )}

          <Paper
            variant="modern"
            elevation={0}
            sx={{
              p: { xs: 2, sm: 4 },
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            {verificationToken ? (
              <Box component="form" onSubmit={handleVerifyCode}>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Alert severity="success" sx={{ mb: 1 }}>
                      Registration successful. We sent a 6-digit verification code to {verificationEmail || formData.email}.
                    </Alert>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
                      Enter your 6-digit verification code
                    </Typography>
                    <Box
                      sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}
                      onPaste={handleVerificationCodePaste}
                    >
                      {verificationCodeDigits.map((digit, idx) => (
                        <TextField
                          key={idx}
                          value={digit}
                          error={Boolean(verificationError)}
                          onChange={(e) => handleVerificationDigitChange(idx, e.target.value)}
                          onKeyDown={(e) => handleVerificationDigitKeyDown(idx, e)}
                          inputRef={(el) => {
                            verificationInputsRef.current[idx] = el;
                          }}
                          disabled={isVerifyingCode}
                          inputProps={{
                            maxLength: 1,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: {
                              textAlign: 'center',
                              fontSize: '1.4rem',
                              fontWeight: 700,
                              padding: '12px 0'
                            }
                          }}
                          sx={{
                            width: { xs: 44, sm: 52 },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0.75rem',
                              backgroundColor: 'background.paper'
                            }
                          }}
                        />
                      ))}
                    </Box>
                    {/* {verificationError && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ mt: 1.5, textAlign: 'center', fontWeight: 600 }}
                      >
                          <span>{verificationError}</span>
                      </Typography>
                    )}          */}
                  </Grid>
                  <Grid size={12}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="gradient"
                      size="large"
                      disabled={isVerifyingCode || verificationCodeDigits.join('').length !== 6}
                      sx={{ py: 1.5 }}
                    >
                      {isVerifyingCode ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} color="inherit" />
                          <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                            Verifying code...
                          </Typography>
                        </Box>
                      ) : (
                        'Verify and Continue'
                      )}
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={isVerifyingCode}
                      onClick={handleResendCode}
                    >
                      Resend code
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Account type comes first so it also applies to Google signup */}
                {/* Account type: one compact row (decided for outreach recipients) */}
                {outreachRefCode ? (
                  <Grid size={12}>
                    {/* <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        p: 1.25,
                        borderRadius: '0.5rem',
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        color: 'text.primary',
                      }}
                    >
                      <HomeIcon fontSize="small" color="primary" />
                      <Typography variant="body2">
                        Signing up as a <strong>home seller</strong>
                        {outreachData?.target?.address ? ` for ${outreachData.target.address}` : ''}.
                      </Typography>
                    </Stack> */}
                  </Grid>
                ) : (
                  <Grid size={12} sx={{mb:5}}>
                    {(() => {
                      const selectedRole =
                        SIGNUP_ROLES.find((role) => role.value === formData.primary_role) || SIGNUP_ROLES[0];
                      const selectRole = (value) => {
                        if (value === formData.primary_role) return;
                        setFormData({ ...formData, primary_role: value });
                        if (errors.license_number) setErrors((prev) => ({ ...prev, license_number: '' }));
                      };
                      return (
                        <>
                          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                            <Typography
                              variant="overline"
                              sx={{ color: 'text.secondary', letterSpacing: '.1em', lineHeight: 1.4 }}
                            >
                              I am a
                            </Typography>
                            <Typography
                              key={selectedRole.value}
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: selectedRole.accent,
                                animation: 'roleLabelIn 220ms ease-out',
                                '@keyframes roleLabelIn': {
                                  from: { opacity: 0, transform: 'translateY(3px)' },
                                  to: { opacity: 1, transform: 'none' },
                                },
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                              }}
                            >
                              {selectedRole.label}
                            </Typography>
                          </Stack>

                          <Box
                            role="radiogroup"
                            aria-label="Account type"
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                              gap: { xs: 1, sm: 1.5 },
                            }}
                          >
                            {SIGNUP_ROLES.map((role) => {
                              const selected = role.value === formData.primary_role;
                              const accent = role.accent;
                              return (
                                <Box
                                  key={role.value}
                                  role="radio"
                                  aria-checked={selected}
                                  tabIndex={selected ? 0 : -1}
                                  onClick={() => selectRole(role.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      selectRole(role.value);
                                    }
                                  }}
                                  sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    gap: 0.75,
                                    px: { xs: 0.75, sm: 1.5 },
                                    py: { xs: 1.5, sm: 1.75 },
                                    borderRadius: '14px',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    border: `1.5px solid ${selected ? accent : theme.palette.divider}`,
                                    background: selected
                                      ? `linear-gradient(160deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.04)} 100%)`
                                      : theme.palette.background.paper,
                                    boxShadow: selected ? `0 10px 24px -10px ${alpha(accent, 0.55)}` : 'none',
                                    transform: selected ? 'translateY(-2px)' : 'none',
                                    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease',
                                    outline: 'none',
                                    '&:hover': {
                                      borderColor: accent,
                                      transform: 'translateY(-2px)',
                                      boxShadow: `0 10px 24px -12px ${alpha(accent, 0.5)}`,
                                    },
                                    '&:focus-visible': {
                                      boxShadow: `0 0 0 3px ${alpha(accent, 0.35)}`,
                                    },
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none', transform: 'none' },
                                  }}
                                >
                                  {/* Selected badge */}
                                  <Box
                                    aria-hidden
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      width: 18,
                                      height: 18,
                                      borderRadius: '50%',
                                      bgcolor: accent,
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transform: selected ? 'scale(1)' : 'scale(0)',
                                      opacity: selected ? 1 : 0,
                                      transition: 'transform 200ms cubic-bezier(.2,.9,.3,1.4), opacity 150ms',
                                      '& svg': { fontSize: 14 },
                                    }}
                                  >
                                    <CheckCircleIcon />
                                  </Box>

                                  {/* Icon disc */}
                                  <Box
                                    sx={{
                                      width: { xs: 40, sm: 44 },
                                      height: { xs: 40, sm: 44 },
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: selected ? '#fff' : accent,
                                      background: selected
                                        ? `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.75)} 100%)`
                                        : alpha(accent, 0.1),
                                      boxShadow: selected ? `0 6px 14px -6px ${alpha(accent, 0.7)}` : 'none',
                                      transition: 'background 180ms ease, color 180ms ease, box-shadow 180ms ease',
                                      '& svg': { fontSize: { xs: 20, sm: 22 } },
                                    }}
                                  >
                                    {role.icon}
                                  </Box>

                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                        color: selected ? accent : 'text.primary',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                      }}
                                    >
                                      {role.label}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        color: 'text.secondary',
                                        lineHeight: 1.25,
                                        mt: 0.25,
                                        fontSize: { xs: '0.66rem', sm: '0.72rem' },
                                      }}
                                    >
                                      {role.tagline}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>

                          {/* What the selected type gets — swaps with a short fade */}
                          {/* <Box
                            key={`desc-${selectedRole.value}`}
                            sx={{
                              mt: 1.25,
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              borderLeft: `3px solid ${selectedRole.accent}`,
                              bgcolor: alpha(selectedRole.accent, 0.06),
                              animation: 'roleDescIn 240ms ease-out',
                              '@keyframes roleDescIn': {
                                from: { opacity: 0, transform: 'translateY(-4px)' },
                                to: { opacity: 1, transform: 'none' },
                              },
                              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
                              {selectedRole.description}
                            </Typography>
                          </Box> */}
                        </>
                      );
                    })()}
                  </Grid>
                )}
                <Grid size={12}>
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: 'text.secondary', letterSpacing: '.1em', lineHeight: 1.4 }}
                    >
                      Sign Up
                    </Typography>
                  </Stack>
                </Grid>

                {/* OAuth Providers Section (after the account type so the choice rides along) */}
                {availableProviders.length > 0 && (
                  <>
                    <Grid size={12}>
                      <OAuthButtonGroup
                        providers={availableProviders}
                        onProviderSelect={handleOAuthSignup}
                        disabled={oauthLoading}
                        orientation="vertical"
                      />
                      {/* {!outreachRefCode && (formData.primary_role === 'Agent' || formData.primary_role === 'Lawyer') && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          Signing up with Google as a {formData.primary_role.toLowerCase()} — you'll add your licence number right after.
                        </Typography>
                      )} */}
                    </Grid>

                    <Grid size={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ px: 2, color: '#64748b' }}>
                          OR
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    </Grid>
                  </>
                )}

                {/* Fallback: Show message if no OAuth providers are available */}
                {availableProviders.length === 0 && (
                  <Grid size={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Social signup is currently unavailable. Please use email and password to create an account.
                    </Alert>
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => {
                      setFormData({...formData, first_name: e.target.value});
                      if (errors.first_name) {
                        setErrors(prev => ({...prev, first_name: ''}));
                      }
                    }}
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) => {
                      setFormData({...formData, last_name: e.target.value});
                      if (errors.last_name) {
                        setErrors(prev => ({...prev, last_name: ''}));
                      }
                    }}
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({...formData, email: e.target.value});
                      // Clear error when user starts typing
                      if (errors.email) {
                        setErrors(prev => ({...prev, email: ''}));
                      }
                      if (apiError) {
                        setApiError(null);
                      }
                    }}
                    error={!!errors.email}
                    helperText={errors.email}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    error={!!errors.password}
                    helperText={errors.password}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />

                  {/* Password Strength Indicator */}
                  {passwordStrength && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Password Strength:
                        </Typography>
                        <Chip 
                          label={passwordStrength.strength}
                          color={getPasswordStrengthColor(passwordStrength.strength)}
                          size="small"
                        />
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={getPasswordStrengthProgress(passwordStrength.score, passwordStrength.max_score)}
                        color={getPasswordStrengthColor(passwordStrength.strength)}
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          mb: 1 
                        }}
                      />

                      {/* Password Requirements - Only show unmet criteria */}
                      <Box sx={{ mt: 1 }}>
                        {Object.entries(passwordStrength.checks)
                          .filter(([check, passed]) => !passed) // Only show failed checks
                          .map(([check, passed]) => {
                            const checkLabels = {
                              length_ok: 'Not long enough',
                              has_uppercase: 'Uppercase letter (A-Z)',
                              has_lowercase: 'Lowercase letter (a-z)', 
                              has_numbers: 'Number (0-9)',
                              has_special: 'Special character (!@#$%^&*)',
                              no_common_patterns: 'No common patterns'
                            };

                            return (
                              <Box key={check} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CancelIcon color="error" sx={{ fontSize: 16 }} />
                                <Typography 
                                  variant="body2" 
                                  color="error.main"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  {checkLabels[check]}
                                </Typography>
                              </Box>
                            );
                          })}
                        
                        {/* Show success message when all criteria are met */}
                        {/* {Object.values(passwordStrength.checks).every(passed => passed) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                            <Typography 
                              variant="body2" 
                              color="success.main"
                              sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                            >
                              All password requirements met!
                            </Typography>
                          </Box>
                        )} */}
                      </Box>

                      {/* Recommendations */}
                      {passwordStrength.recommendations && passwordStrength.recommendations.length > 0 && (
                        <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            <strong>Suggestions:</strong> {passwordStrength.recommendations.join(', ')}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                      },
                    }}
                  />
                </Grid>
                {/* Professional details: licence (+ brokerage for agents) in one row */}
                {!outreachRefCode && (formData.primary_role === 'Agent' || formData.primary_role === 'Lawyer') && (
                  <>
                    <Grid size={{ xs: 12, sm: formData.primary_role === 'Agent' ? 6 : 12 }}>
                      <TextField
                        fullWidth
                        label={formData.primary_role === 'Agent' ? 'RECO License Number' : 'LSO License Number'}
                        value={formData.license_number}
                        onChange={(e) => {
                          setFormData({...formData, license_number: e.target.value});
                          if (errors.license_number) setErrors((prev) => ({ ...prev, license_number: '' }));
                        }}
                        error={!!errors.license_number}
                        helperText={errors.license_number || 'Used only to verify your account'}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.5rem',
                          },
                        }}
                      />
                    </Grid>
                    {formData.primary_role === 'Agent' && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Brokerage"
                          value={formData.brokerage}
                          onChange={(e) => setFormData({...formData, brokerage: e.target.value})}
                          error={!!errors.brokerage}
                          helperText={errors.brokerage}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0.5rem',
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{' '}
                        <Link
                          component={RouterLink}
                          to="/terms-of-service"
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                          Terms of Service
                        </Link>
                        {' '}and{' '}
                        <Link
                          component={RouterLink}
                          to="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                  />
                  {errors.terms && (
                    <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                      {errors.terms}
                    </Typography>
                  )}
                </Grid>
                {errors.general && (
                  <Grid size={12}>
                    <Typography color="error" variant="body2">
                      {errors.general}
                    </Typography>
                  </Grid>
                )}
                <Grid size={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="gradient"
                    size="large"
                    disabled={
                      isSubmitting ||
                      !acceptTerms || 
                      (passwordStrength && !passwordStrength.is_secure)
                    }
                    sx={{
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} color="inherit" />
                        Creating account...
                      </Box>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
            )}
          </Paper>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                underline="hover"
                sx={{ fontWeight: 600, color: 'primary.main' }}
              >
                Log in
              </Link>
            </Typography>
          </Box>
        </Container>
      </Box>

      <PropertyCreationProgressModal
        {...propertyCreationProgress.modalProps}
      />

    </Box>
  );
};

export default Signup;
